import { groupBySection } from '@/components/Sidebar';
import { resources } from '@/resources';
import { useResourceCount } from '@/lib/use-resource-count';
import { cn } from '@/lib/utils';
import { Link } from 'react-router';
import { PageHeader } from '@/components/PageHeader';
import {
  Bot,
  ChartColumn,
  CircleCheck,
  Clock,
  Database,
  FileText,
  Fingerprint,
  Globe,
  KeyRound,
  Layers,
  PenLine,
  ScrollText,
  Server,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  type LucideIcon,
} from 'lucide-react';

// Keyed by resource name. `dashboard.test.ts` pins that every resource in
// `resources.ts` has an entry in BOTH maps — a row without them renders as a
// bare title (how the Usage card shipped in M33).
export const SUMMARIES: Record<string, string> = {
  users: 'Manage admin and member identities.',
  'api-keys': 'Mint, list, and revoke hub API keys.',
  permissions: 'Edit Zanzibar tuples (object → relation → subject).',
  'identity-providers': 'Configure login SSO and the Drive connector OAuth app.',
  'web-research': 'Configure the web search, extract, and browse providers.',
  models: 'Register named LLM models the hub can use.',
  assignments: 'Bind each hub purpose to a registered model.',
  'db-connectors': 'Connect external databases and curate their semantic layers.',
  'mcp-servers': 'Register MCP servers, choose how users connect, approve their tools.',
  documents: 'Browse and search ingested documents across tracks.',
  tracks: 'Configure registered tracks and their API URLs.',
  prompts: 'Curate system prompts and versions.',
  approvals: 'Review and resolve pending agent approvals.',
  schedules: 'Inspect scheduled actions and their runs.',
  audit: 'Every recorded event — who did what, when, on which record, old and new value.',
  usage: 'Recorded activity from the audit log — who and what the hub is used for.',
  settings: 'Hub-wide configuration and appearance.',
};

export const ICONS: Record<string, LucideIcon> = {
  users: Users,
  'api-keys': KeyRound,
  permissions: ShieldCheck,
  'identity-providers': Fingerprint,
  'web-research': Globe,
  models: Bot,
  assignments: SlidersHorizontal,
  'db-connectors': Database,
  'mcp-servers': Server,
  documents: FileText,
  tracks: Layers,
  prompts: PenLine,
  approvals: CircleCheck,
  schedules: Clock,
  audit: ScrollText,
  usage: ChartColumn,
  settings: Settings,
};

// The dashboard shows the sections as grouped rows, not one card each: 16
// cards in a 3-wide grid scrolled past the fold, and every card carried an
// empty content block below its text. The groups are the sidebar's own
// (`groupBySection`), so the dashboard reads like the nav.

export interface DashboardGroup<T extends { name: string }> {
  label: string;
  items: T[];
}

// Pure split of the sidebar groups into two columns, keeping their order:
// groups fill the first column until it holds at least half the rows, the
// rest go to the second. Pinned by dashboard.test.ts.
export function dashboardColumns<T extends { name: string }>(all: readonly T[]): DashboardGroup<T>[][] {
  const groups = groupBySection(all);
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const left: DashboardGroup<T>[] = [];
  let rows = 0;
  while (groups.length > 0 && rows < total / 2) {
    const g = groups.shift() as DashboardGroup<T>;
    left.push(g);
    rows += g.items.length;
  }
  return [left, groups];
}

function DashboardRow({ name, label, to, count }: { name: string; label: string; to: string; count?: number }) {
  const Icon = ICONS[name];
  const summary = SUMMARIES[name] ?? '';
  return (
    <Link
      to={to}
      title={summary}
      className={cn(
        'grid h-11 items-center gap-3 border-t px-3.5 text-sm text-foreground no-underline transition-colors first:border-t-0',
        'grid-cols-[16px_minmax(0,1fr)_auto] md:grid-cols-[16px_150px_minmax(0,1fr)_auto]',
        'hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
      )}
    >
      {Icon ? <Icon className="size-4 text-muted-foreground" aria-hidden="true" /> : <span />}
      <span className="truncate font-semibold">{label}</span>
      <span className="hidden truncate text-[13px] text-muted-foreground md:block">{summary}</span>
      <span className="min-w-5 text-right text-xs font-semibold tabular-nums text-muted-foreground">
        {count ?? ''}
      </span>
    </Link>
  );
}

export function Dashboard() {
  // Fixed hook set — live totals for the whole-collection resources.
  const counts: Record<string, number | undefined> = {
    users: useResourceCount('users'),
    'api-keys': useResourceCount('api-keys'),
    tracks: useResourceCount('tracks'),
    prompts: useResourceCount('prompts'),
    permissions: useResourceCount('permissions'),
    models: useResourceCount('models'),
  };
  const columns = dashboardColumns(resources);

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" description="Pick a section to manage." />
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
        {columns.map((groups, i) => (
          <div key={i} className="flex flex-col gap-5">
            {groups.map((g) => (
              <section key={g.label} aria-label={g.label} className="flex flex-col gap-2">
                <h2 className="pl-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {g.label}
                </h2>
                <div className="overflow-hidden rounded-lg border bg-card text-card-foreground">
                  {g.items.map((r) => (
                    <DashboardRow
                      key={r.name}
                      name={r.name}
                      label={r.meta?.label ?? r.name}
                      to={r.list as string}
                      count={counts[r.name]}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
